import * as React from "react";
import { QuestionType } from "../../../types";
import isEmailAddressValid from "../../../helpers/isEmailAddressValid";
import Question from "./Question";
import Button from "../../Button";
import StoreContainer from "../../../stores/StoreContainer";
import { useCallback, useEffect, useRef } from "react";
import useConfig from "../../../hooks/useConfig";
import isSectionVisible from "../helpers/isSectionVisible";

interface Props {
  pagedQuestions: any;
  currentPage: any;
  answers: any;
  errors: any;
  onChange: any;
  onChangeSection: any;
  visibleSections: any;
  onValidationError: any;
  onSubmit: any;
  totalPages: any;
  submittingSurvey: any;
  submitButtonText: any;
  onPage: any;
}

function SurveyForm(props: Props) {
  let formRef: any;
  const previousErrorsRef = useRef();
  const config = useConfig();

  function componentDidMount() {
    try {
      formRef.elements[0].focus();
    } catch {
      // just swallow the error, focus will fail in tests
    }
  }

  const focusFirstError = useCallback(() => {
    try {
      // focus first error element
      if (!formRef) {
        return;
      }
      const errorElements = formRef.querySelectorAll("[aria-invalid='true']");

      if (errorElements.length > 0) {
        errorElements[0].focus();
      }
    } catch (err) {
      console.error("failed to focus first error");
      console.error(err);
    }
  }, [formRef]);

  useEffect(() => {
    if (previousErrorsRef.current !== props.errors) {
      focusFirstError();
    }
    formRef.elements[0].focus();
    previousErrorsRef.current = props.errors;
  }, [focusFirstError, formRef, props.errors]);

  function getQuestionsForCurrentPage() {
    return props.pagedQuestions[props.currentPage] || [];
  }

  function getAnswer(question: any) {
    return props.answers[question.QuestionID] || null;
  }

  function renderQuestions() {
    return getQuestionsForCurrentPage().map((question: any) => (
      <Question
        key={question.QuestionID}
        question={question}
        answer={getAnswer(question)}
        onChange={props.onChange}
        error={props.errors[question.QuestionID]}
        onChangeSection={props.onChangeSection}
        visibleSections={props.visibleSections}
      />
    ));
  }

  function validateEmail(question: any) {
    const answers = props.answers;
    const answer = answers[question.QuestionID];

    if (!answer) {
      return true;
    }

    return isEmailAddressValid(answer);
  }

  function isValid() {
    const answers = props.answers;

    const validateField = (question: any) => {
      if (question.QuestionType === QuestionType.Email) {
        if (validateEmail(question) === false) {
          return { success: false, message: question.InvalidEmailMessage };
        }
      }

      if (
        question.QuestionType === QuestionType.VisitorPhone &&
        StoreContainer.SettingStore.getClickToCall()
      ) {
        question.Required = true;
      }

      if (!question.Required) {
        return { success: true };
      }

      let answer = answers[question.QuestionID];

      if (answer && answer.hasOwnProperty("answer")) {
        answer = answer.answer;
      }

      if (answer === null || answer === undefined || answer === "") {
        return {
          success: false,
          message: question.ValidationFailedMessage || "Field required"
        };
      }

      return { success: true };
    };

    let valid = true;
    const errors: any = {};

    getQuestionsForCurrentPage().forEach((question: any) => {
      if (
        isSectionVisible(question.SectionID, props.visibleSections) === false
      ) {
        return true;
      }

      const result = validateField(question);

      if (result.success) {
        return;
      }

      valid = false;
      errors[question.QuestionID] = result.message;
    });

    props.onValidationError(errors);
    return valid;
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (isValid() === false) {
      return;
    }

    props.onSubmit();
  }

  function renderPrevButton() {
    if (props.currentPage < 2) {
      return null;
    }

    const prev = () => {
      props.onPage(-1);
    };

    return (
      <Button variant="primary" onClick={prev}>
        {config.SurveyPreviousPageButtonText}
      </Button>
    );
  }

  function renderNextButton() {
    if (props.currentPage >= props.totalPages) {
      return null;
    }

    const next = () => {
      // validate the current page before allowing
      // the user to continue to the next page.
      if (!isValid()) {
        return;
      }

      props.onPage(1);
    };

    return (
      <Button variant="primary" onClick={next} className="velaro-pull-right">
        {config.SurveyNextPageButtonText}
      </Button>
    );
  }

  function renderSubmitButton() {
    if (props.currentPage < props.totalPages) {
      return null;
    }

    return (
      <Button
        variant="primary"
        type="submit"
        className="velaro-pull-right"
        disabled={props.submittingSurvey}
      >
        {props.submitButtonText || "Submit"}
      </Button>
    );
  }

  function renderButtons() {
    let pageButtons = null;

    if (props.totalPages > 1) {
      pageButtons = (
        <React.Fragment>
          {renderPrevButton()}
          {renderNextButton()}
        </React.Fragment>
      );
    }

    return (
      <div className="velaro-survey-actions">
        {pageButtons}
        {renderSubmitButton()}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} ref={(el) => (formRef = el)} noValidate>
      <div>{renderQuestions()}</div>
      {renderButtons()}
    </form>
  );
}

export default SurveyForm;
